import React, { useState, useEffect } from "react";
import {
  Table,
  Modal,
  Button,
  Form,
  Alert,
  Badge,
  InputGroup,
  FormControl,
  Container,
  Row,
  Col,
} from "react-bootstrap";
import {
  FaTrashAlt,
  FaEdit,
  FaEye,
  FaSearch,
  FaPlus,
  FaUserTie,
} from "react-icons/fa";
import { APICall } from "../../Utls/Api";

const ManagerView = () => {
  // State management
  const [managers, setManagers] = useState([]);
  const [filteredManagers, setFilteredManagers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [managerData, setManagerData] = useState({
    fullname: "",
    email: "",
    password: "",
    department: "",
    department_id: "",
  });
  const [alldepartments, setAllDepartments] = useState([]);
  const [currentManager, setCurrentManager] = useState(null);
  const [modalType, setModalType] = useState(null); // 'view', 'add', 'edit', 'delete'
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "success",
  });

  const getDepartments = async () => {
    try {
      const response = await APICall("auth/department/all", "GET");

      setAllDepartments(response.departments || []);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };
  // Fetch managers on mount
  useEffect(() => {
    fetchManagers();
    getDepartments();
  }, []);

  // Filter managers when search term changes
  useEffect(() => {
    const filtered = managers.filter(
      (manager) =>
        manager.fullname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        manager.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredManagers(filtered);
  }, [searchTerm, managers]);

  const fetchManagers = async () => {
    try {
      const response = await APICall("auth/manager/all", "GET");
      setManagers(response.managers || []);
    } catch (error) {
      showAlert("Failed to fetch managers", "danger");
      console.error("Error:", error);
    }
  };

  const showAlert = (message, variant = "success") => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ ...alert, show: false }), 3000);
  };

  const openModal = (type, manager = null) => {
    setCurrentManager(manager);
    setModalType(type);

    if (type === "edit" && manager) {
      setManagerData({
        fullname: manager.fullname,
        email: manager.email,
        password: "",
        department: manager.department || "",
        department_id: manager.department_id || "",
      });
    } else if (type === "add") {
      setManagerData({
        fullname: "",
        email: "",
        password: "",
        department: "",
        department_id: "",
      });
    }
  };

  const closeModal = () => {
    setModalType(null);
    setCurrentManager(null);
  };

  const handleDelete = async () => {
    try {
      const data = {
        id: currentManager._id,
      };
      await APICall(`auth/manager/delete`, "POST", data);
      fetchManagers();
      showAlert("Manager deleted successfully");
      closeModal();
    } catch (error) {
      showAlert("Failed to delete manager", "danger");
      console.error("Error:", error);
    }
  };

  const handleSubmit = async () => {
    try {
      if (modalType === "edit") {
        // Include the manager ID in the update data
        const updateData = {
          ...managerData,
          id: currentManager._id, // Add the manager ID
        };
        await APICall(`auth/manager/update`, "POST", updateData);
        showAlert("Manager updated successfully");
      } else {
        await APICall("auth/manager/create", "POST", {
          ...managerData,
          name: managerData.fullname,
          department_id: managerData.department_id,
        });
        showAlert("Manager added successfully");
      }
      fetchManagers();
      closeModal();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message;
      showAlert(
        `Failed to ${
          modalType === "edit" ? "update" : "add"
        } manager: ${errorMsg}`,
        "danger"
      );
      console.error("Error:", error);
    }
  };

  // Custom Avatar Component
  const Avatar = ({ name, size = 40, className = "" }) => {
    const initials = name ? name.charAt(0).toUpperCase() : "";
    return (
      <div
        className={`rounded-circle bg-primary text-white d-flex align-items-center justify-content-center ${className}`}
        style={{
          width: size,
          height: size,
          fontSize: size * 0.5,
          fontWeight: "bold",
        }}
      >
        {initials}
      </div>
    );
  };

  return (
    <Container fluid className="p-3 p-md-4">
      {/* Alert Notification */}
      {alert.show && (
        <Alert
          variant={alert.variant}
          className="fade-in"
          onClose={() => setAlert({ ...alert, show: false })}
          dismissible
        >
          {alert.message}
        </Alert>
      )}

      {/* Header Section */}
      <Row className="mb-4 align-items-center">
        <Col xs={12} md={6} className="mb-3 mb-md-0">
          <div className="d-flex align-items-center">
            <FaUserTie className="me-2" size={24} />
            <h4 className="m-0">Managers</h4>
            <Badge bg="secondary" className="ms-2">
              {managers.length}
            </Badge>
          </div>
        </Col>

        <Col xs={12} md={6}>
          <div className="d-flex flex-column flex-md-row gap-2 justify-content-end">
            <div>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <FormControl
                  placeholder="Search managers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </div>

            <div>
              <Button
                variant="primary"
                onClick={() => openModal("add")}
                className="d-flex align-items-center justify-content-center"
              >
                <FaPlus className="me-2" />
                <span className="d-none d-md-inline">Add Manager</span>
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Modern Table */}
      <div className="table-responsive rounded-3 shadow-sm">
        <Table hover className="m-0">
          <thead className="table-dark">
            <tr>
              <th style={{ width: "5%" }} className="d-none d-md-table-cell">
                #
              </th>
              <th style={{ width: "25%" }}>Manager</th>
              <th style={{ width: "25%" }} className="d-none d-md-table-cell">
                Email
              </th>
              <th style={{ width: "20%" }} className="d-none d-lg-table-cell">
                Department
              </th>
              <th style={{ width: "15%" }} className="d-none d-md-table-cell">
                Status
              </th>
              <th style={{ width: "15%" }} className="text-end">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredManagers.length > 0 ? (
              filteredManagers.map((manager, index) => (
                <tr key={manager._id} className="align-middle">
                  <td className="d-none d-md-table-cell">{index + 1}</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <Avatar
                        name={manager.fullname}
                        className="me-2 me-md-3"
                        size={36}
                      />
                      <div>
                        <div className="fw-semibold">{manager.fullname}</div>
                        <small className="text-muted d-md-none">
                          {manager.email}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td className="d-none d-md-table-cell">{manager.email}</td>
                  <td className="d-none d-lg-table-cell">
                    <Badge bg="info" pill>
                      {manager.department_id.name || "Not Assigned"}
                    </Badge>
                  </td>
                  <td className="d-none d-md-table-cell">
                    <Badge bg="success" pill>
                      Active
                    </Badge>
                  </td>
                  <td className="text-end">
                    <div className="d-flex justify-content-end gap-1 gap-md-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => openModal("view", manager)}
                        title="View"
                        className="p-1 p-md-2"
                      >
                        <FaEye />
                      </Button>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => openModal("edit", manager)}
                        title="Edit"
                        className="p-1 p-md-2"
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => openModal("delete", manager)}
                        title="Delete"
                        className="p-1 p-md-2"
                      >
                        <FaTrashAlt />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center py-4">
                  {managers.length === 0 ? (
                    <div className="text-muted">
                      <FaUserTie size={48} className="mb-3" />
                      <h5>No managers found</h5>
                      <p>Click "Add Manager" to create your first manager</p>
                    </div>
                  ) : (
                    <div className="text-muted">
                      <FaSearch size={48} className="mb-3" />
                      <h5>No matching managers found</h5>
                      <p>Try adjusting your search query</p>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* View Modal */}
      <Modal show={modalType === "view"} onHide={closeModal} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>Manager Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentManager && (
            <div className="manager-details">
              <div className="text-center mb-4">
                <Avatar
                  name={currentManager.fullname}
                  className="mx-auto mb-3"
                  size={80}
                />
                <h4>{currentManager.fullname}</h4>
                <p className="text-muted">{currentManager.email}</p>
              </div>

              <div className="details-list">
                <div className="detail-item d-flex justify-content-between py-2 border-bottom">
                  <span className="detail-label fw-bold">Manager ID</span>
                  <span className="detail-value text-muted">
                    {currentManager._id}
                  </span>
                </div>
                <div className="detail-item d-flex justify-content-between py-2 border-bottom">
                  <span className="detail-label fw-bold">Department</span>
                  <span className="detail-value">
                    <Badge bg="info">{currentManager.department_id.name  || "Not Assigned"}</Badge>
                  </span>
                </div>
                <div className="detail-item d-flex justify-content-between py-2 border-bottom">
                  <span className="detail-label fw-bold">Status</span>
                  <span className="detail-value">
                    <Badge bg="success">Active</Badge>
                  </span>
                </div>
                <div className="detail-item d-flex justify-content-between py-2">
                  <span className="detail-label fw-bold">Created At</span>
                  <span className="detail-value text-muted">
                    {new Date(currentManager.createdAt).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add/Edit Modal */}
      <Modal
        show={modalType === "add" || modalType === "edit"}
        onHide={closeModal}
        centered
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            {modalType === "edit" ? "Edit Manager" : "Add New Manager"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter full name"
                value={managerData.fullname}
                onChange={(e) =>
                  setManagerData({ ...managerData, fullname: e.target.value })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={managerData.email}
                onChange={(e) =>
                  setManagerData({ ...managerData, email: e.target.value })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Department</Form.Label>
              <Form.Select
                value={managerData.department_id}
                onChange={(e) => {
                  const selectedDept = alldepartments.find(dept => dept._id === e.target.value);
                  setManagerData({ 
                    ...managerData, 
                    department_id: e.target.value,
                    department: selectedDept ? selectedDept.name : ""
                  });
                }}
                required
              >
                <option value="">Select Department</option>
                {alldepartments.map((dept) => (
                  <option key={dept._id} value={dept._id}>
                    {dept.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {modalType === "add" && (
              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Enter password"
                  value={managerData.password}
                  onChange={(e) =>
                    setManagerData({ ...managerData, password: e.target.value })
                  }
                  required
                  minLength="8"
                />
                <Form.Text className="text-muted">
                  Minimum 8 characters
                </Form.Text>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={
              !managerData.fullname ||
              !managerData.email ||
              !managerData.department_id ||
              (modalType === "add" && !managerData.password)
            }
          >
            {modalType === "edit" ? "Update Manager" : "Add Manager"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={modalType === "delete"} onHide={closeModal} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentManager && (
            <div className="text-center">
              <div className="avatar-circle-lg bg-danger text-white mx-auto mb-3 d-flex align-items-center justify-content-center">
                <FaTrashAlt size={24} />
              </div>
              <h5>Delete {currentManager.fullname}?</h5>
              <p className="text-muted">
                This action cannot be undone. All data associated with this
                manager will be permanently removed.
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete Manager
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Custom CSS */}
      <style jsx>{`
        .fade-in {
          animation: fadeIn 0.3s;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .avatar-circle-lg {
          width: 80px;
          height: 80px;
          font-size: 32px;
        }
        .table-responsive {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
      `}</style>
    </Container>
  );
};

export default ManagerView;
